import { Component } from '@angular/core'
import { Store } from '@ngrx/store'
import { take } from 'rxjs'
import { AppState } from 'src/app/store/state/app.state'
import {
  SummaryDataTableComponent,
  SummaryDataTableData,
  SummaryDataTableOptions
} from '../../common/summary-data-table/summary-data-table.component'
import { EmployeeService } from 'src/app/services/employee/employee.service'
import { EmployeeAPIModel, PaginatedAPI, employeeAPIModel } from 'src/app/models/api.model'
import {
  DOB,
  EMP_STATUS,
  ID,
  JOINING_DATE,
  NAME,
  COMPANY_ENTITY_DISPLAY_ID,
  AADHAR_NUMBER as AADHAR_NUMBER_UI_COLUMN,
  UAN_NUMBER as UAN_NUMBER_UI_COLUMN,
  PF_NUMBER as PF_NUMBER_UI_COLUMN
} from 'src/app/constants/ui.columns'
import { getAllEmployeeDetails } from 'src/app/store/selector/employee.selector'
import { getSelectedCompaniesFromState } from 'src/app/store/selector/initialize.selector'
import { Observable } from 'rxjs'
import { load_employee_summary_action } from 'src/app/store/action/employee.action'
import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'
import { DocumentService } from 'src/app/services/common/document.service'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { WebService } from 'src/app/services/api/web.service'
import { EmployeeValidator } from '../../import/validators/employee-validator.base'
import { UpdateEmployeeValidator } from '../../import/validators/update-employee.validator'
import { EmployeeValidatorFactory } from '../../import/validators/employee-validator-factory'

@Component({
  selector: 'app-employee-summary',
  templateUrl: './employee-summary.component.html',
  styleUrls: ['./employee-summary.component.scss']
})
export class EmployeeSummaryComponent {
  allEmployeeList$: Observable<PaginatedAPI<employeeAPIModel>>
  summaryOptions: SummaryDataTableOptions
  summaryData: SummaryDataTableData<employeeAPIModel>
  queryParamValues = {
    pageNo: 0,
    pageLimit: SummaryDataTableComponent.defaultLimitDropdownValue,
    sortBy: 'firstName',
    sortOrder: 'ASC',
    mapReferenceDocuments: 'false'
  }
  searchRequestBody: any = {
    firstName: '',
    dob: null,
    doj: null,
    designation: '',
    location: '',
    departmentName: '',
    status: ''
  }
  showImport: boolean = false
  showUpdateImport: boolean = false

  importValidator: EmployeeValidator
  updateImportValidator: EmployeeValidator
  isRehireModalVisible: boolean = false
  currentEmployeeDetails: EmployeeAPIModel

  isDragging = false
  dragOffset = { x: 0, y: 0 }

  constructor(
    private readonly appState: Store<AppState>,
    private readonly employeeService: EmployeeService,
    private readonly documentService: DocumentService,
    private readonly webService: WebService,
    private readonly validatorFactory: EmployeeValidatorFactory
  ) {
    this.validatorFactory.create(null, true).subscribe(validator => {
      this.importValidator = validator
    })
    this.updateImportValidator = new UpdateEmployeeValidator(employeeService, null, webService)

    this.allEmployeeList$ = this.appState.select(getAllEmployeeDetails())
    this.summaryOptions = {
      downloadable: true,
      additionalButtons: false,
      contentSelectable: true,
      contentSelector: false,
      filterable: true,
      formattedContent: false,
      pageable: true,
      sortable: true,
      quickFilters: true
    }
  }
  ngOnInit(): void {
    this.appState.select(getSelectedCompaniesFromState).pipe(take(1)).subscribe(selectedCompanies => {
      if (selectedCompanies && selectedCompanies.length > 0) {
        this.loadSummaryData()
      }
    })
    this.summaryData = {
      content$: this.allEmployeeList$,
      headers: [
        COMPANY_ENTITY_DISPLAY_ID,
        ID,
        NAME,
        DOB,
        { ...JOINING_DATE, onClick: employeeDetail => this.openRehireModel(employeeDetail) },
        UAN_NUMBER_UI_COLUMN,
        AADHAR_NUMBER_UI_COLUMN,
        PF_NUMBER_UI_COLUMN,
        EMP_STATUS
      ],
      additionalButtons: [],
      currentSearchParams: this.queryParamValues,
      sortCallback: this.sortCallback,
      checkBoxSelectionIds: [],
      searchCallback: this.searchCallback,
      selectPageCallback: this.selectPageCallback,
      onDropdownLimitChange: this.onDropdownLimitChange,
      downloadDocumentCallback: this.downloadDocumentCallback
    }
  }

  onViewForChange = event => {
    this.queryParamValues.pageLimit = event.target.value
    this.loadSummaryData()
  }
  sortCallback = (columnCode, order) => {
    this.queryParamValues.sortBy = columnCode
    this.queryParamValues.sortOrder = order
    this.loadSummaryData()
  }

  downloadDocumentCallback = documentType => {
    const checkBoxSelectionIds = this.summaryData.checkBoxSelectionIds
    if (checkBoxSelectionIds.length > 0) {
      this.searchRequestBody = {
        ids: checkBoxSelectionIds
      }
    }
    const options = {
      queryParams: this.buildQueryParams(true),
      requestBody: this.searchRequestBody
    }
    if (documentType == 'PDF') {
      const endPoint = `${ServiceUrlConstants.API_EMPLOYEE_SUMMARY_LIST_EXPORT + documentType}`
      this.documentService.downloadDocument(endPoint, options)
    } else if (documentType == 'CSV') {
      const endPoint = `${ServiceUrlConstants.API_EMPLOYEE_SUMMARY_LIST_EXPORT_CSV_XLS + documentType}`
      this.documentService.downloadDocument(endPoint, options)
    } else if (documentType == 'XLSX') {
      this.employeeService.exportToExcel(options, documentType)
    }
  }

  buildQueryParams = (buildForDownload = false): { key: string; value: string }[] => {
    return [
      {
        key: 'pageNo',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.PAGE_NUMBER.toString()
          : this.queryParamValues.pageNo.toString()
      },
      {
        key: 'limit',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.ROW_LIMIT.toString()
          : this.queryParamValues.pageLimit.toString()
      },
      {
        key: 'sortBy',
        value: this.queryParamValues.sortBy
      },
      {
        key: 'sortOrder',
        value: this.queryParamValues.sortOrder
      },
      {
        key: 'mapReferenceDocuments',
        value: this.queryParamValues.mapReferenceDocuments
      }
    ]
  }

  private loadSummaryData() {
    const options = {
      queryParams: this.buildQueryParams(),
      searchRequestBody: this.searchRequestBody
    }
    this.appState.dispatch(load_employee_summary_action(options))
  }

  searchCallback = searchParams => {
    this.queryParamValues.pageNo = 0
    this.searchRequestBody = {
      ...searchParams
    }
    this.loadSummaryData()
  }

  selectPageCallback = pageNum => {
    this.searchRequestBody = {}
    this.queryParamValues.pageNo = pageNum
    this.loadSummaryData()
  }

  private onDropdownLimitChange = (limit: number) => {
    this.queryParamValues.pageLimit = limit
    this.loadSummaryData()
  }

  openRehireModel(employeeDetail: EmployeeAPIModel) {
    if (employeeDetail?.employeeRehireDetails) {
      this.isRehireModalVisible = true
      this.currentEmployeeDetails = employeeDetail
    }
  }

  closeRehireModal() {
    this.isRehireModalVisible = false
  }

  startDragging(event: MouseEvent) {
    event.preventDefault()

    const modalElement = (event.target as HTMLElement).closest('.modal-content')

    const rect = modalElement.getBoundingClientRect()

    this.dragOffset = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    }

    this.isDragging = true

    document.addEventListener('mousemove', this.drag)
    document.addEventListener('mouseup', this.stopDragging)
  }

  drag = (event: MouseEvent) => {
    if (!this.isDragging) return

    const modalElement = document.querySelector('.modal-content') as HTMLElement

    const newLeft = event.clientX - this.dragOffset.x
    const newTop = event.clientY - this.dragOffset.y

    modalElement.style.left = `${newLeft}px`
    modalElement.style.top = `${newTop}px`
    modalElement.style.transform = 'none'
  }

  stopDragging = () => {
    this.isDragging = false

    document.removeEventListener('mousemove', this.drag)
    document.removeEventListener('mouseup', this.stopDragging)
  }
}
